import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import { servicesData } from '../data/servicesData';
import { ArrowRight, Sparkles, X, Phone, User, Mail, MessageCircle, AlertCircle, BookOpen } from 'lucide-react';
import PartnersSection from '../components/PartnersSection';
import TestimonialsSection from '../components/TestimonialsSection';
import WhatsAppButton from '../components/Watsappfloat';
import WhyChooseUs from '../components/WhyChooseUs';
import HomeHeroSection from '../components/HomeHeroSection';
import HomeServicesSection from '../components/HomeServiceSection';
import { motion, AnimatePresence } from 'framer-motion';
import HomeCTA from '../components/HomeCTA';
import EMSPortalDemo from '../components/Emsportaldemo';
import AdminPanelDemo from '../components/AdminPanel';
import OurClientsSection from '../components/OurClientsSection';
const HomePage = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    services: [],
    budget: '',
    message: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const serviceOptions = [
    'Web Development',
    'Mobile Development',
    'UI/UX Design',
    'Cloud Solutions',
    'CRM Solutions',
    'Digital Marketing'
  ];

  useEffect(() => {
    const timer = setTimeout(() => setShowPopup(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  const closePopup = () => {
    setShowPopup(false);
    setFormData({ name: '', email: '', phone: '', services: [], budget: '', message: '' });
    setFormErrors({});
    setSubmitError('');
    setSubmitSuccess(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (formErrors[name]) setFormErrors({ ...formErrors, [name]: '' });
    if (submitError) setSubmitError('');
  };

  const handleServiceToggle = (service) => {
    const updated = formData.services.includes(service)
      ? formData.services.filter(s => s !== service)
      : [...formData.services, service];
    setFormData({ ...formData, services: updated });
    if (formErrors.services) setFormErrors({ ...formErrors, services: '' });
  };

  const handleSubmit = async () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Invalid email';
    if (!formData.phone.trim()) errors.phone = 'Phone is required';
    else if (!/^[0-9]{10}$/.test(formData.phone.replace(/\D/g, ''))) errors.phone = 'Invalid phone';
    if (formData.services.length === 0) errors.services = 'Select at least one service';
    if (!formData.budget.trim()) errors.budget = 'Budget is required';
    if (!formData.message.trim()) errors.message = 'Message is required';

    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }

    setIsSubmitting(true);
    setSubmitError('');
    try {
      const response = await fetch(`${API_URL}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Something went wrong');
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setTimeout(() => closePopup(), 2500);
    } catch (error) {
      setIsSubmitting(false);
      setSubmitError(error.message || 'Failed to send. Please try again.');
    }
  };

  return (
    <div>
      <WhatsAppButton />

      <AnimatePresence>
        {showPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
            onClick={closePopup}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.88, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.88, y: 24 }}
              transition={{ type: 'spring', duration: 0.45, bounce: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              style={{ maxHeight: '92vh' }}
              className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
            >
              {/* Close */}
              <button
                onClick={closePopup}
                className="absolute top-3 right-3 z-20 bg-white/80 dark:bg-gray-800/80 p-1.5 rounded-full text-gray-500 hover:text-red-500 transition-all hover:rotate-90 duration-300 shadow"
              >
                <X size={18} />
              </button>

              {/* Header — fixed at top, no scroll */}
              <div className="flex-shrink-0 bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 px-5 pt-5 pb-4 text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Sparkles className="text-yellow-300" size={16} />
                  <span className="text-white/80 text-xs font-semibold uppercase tracking-widest">One Nest Technologies</span>
                  <Sparkles className="text-yellow-300" size={16} />
                </div>
                <h2 className="text-white text-xl font-black leading-tight">Let's Build Something Great</h2>
                <p className="text-white/70 text-xs mt-1">We'll get back to you within 24 hours ✦</p>
              </div>

              {/* Scrollable form body */}
              <div className="flex-1 overflow-y-auto px-5 py-4">
                {submitSuccess ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-10"
                  >
                    <div className="text-5xl mb-3">✅</div>
                    <h3 className="text-lg font-black text-gray-800 dark:text-gray-100 mb-1">Message Sent!</h3>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">We'll reach out to you soon.</p>
                  </motion.div>
                ) : (
                  <>
                    {submitError && (
                      <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 p-2.5 rounded-lg mb-3 flex items-center gap-2 text-xs"
                      >
                        <AlertCircle size={14} className="flex-shrink-0" />
                        {submitError}
                      </motion.div>
                    )}

                    <div className="space-y-3">
                      {/* Name + Email row */}
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Your Name *</label>
                          <div className="relative">
                            <User className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                            <input
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              placeholder="Full name"
                              className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:outline-none text-xs transition-all dark:bg-gray-800 dark:text-white ${formErrors.name ? 'border-red-400' : 'border-gray-200 dark:border-gray-700 focus:border-violet-500'}`}
                            />
                          </div>
                          {formErrors.name && <p className="text-red-500 text-[10px] mt-0.5">{formErrors.name}</p>}
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Email *</label>
                          <div className="relative">
                            <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              placeholder="you@email.com"
                              className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:outline-none text-xs transition-all dark:bg-gray-800 dark:text-white ${formErrors.email ? 'border-red-400' : 'border-gray-200 dark:border-gray-700 focus:border-violet-500'}`}
                            />
                          </div>
                          {formErrors.email && <p className="text-red-500 text-[10px] mt-0.5">{formErrors.email}</p>}
                        </div>
                      </div>

                      {/* Phone + Budget row */}
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Phone *</label>
                          <div className="relative">
                            <Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                            <input
                              type="tel"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              placeholder="10-digit no."
                              className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:outline-none text-xs transition-all dark:bg-gray-800 dark:text-white ${formErrors.phone ? 'border-red-400' : 'border-gray-200 dark:border-gray-700 focus:border-violet-500'}`}
                            />
                          </div>
                          {formErrors.phone && <p className="text-red-500 text-[10px] mt-0.5">{formErrors.phone}</p>}
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Budget (₹) *</label>
                          <div className="relative">
                            <BookOpen className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                            <input
                              type="text"
                              name="budget"
                              value={formData.budget}
                              onChange={handleChange}
                              placeholder="e.g. ₹50,000"
                              className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:outline-none text-xs transition-all dark:bg-gray-800 dark:text-white ${formErrors.budget ? 'border-red-400' : 'border-gray-200 dark:border-gray-700 focus:border-violet-500'}`}
                            />
                          </div>
                          {formErrors.budget && <p className="text-red-500 text-[10px] mt-0.5">{formErrors.budget}</p>}
                        </div>
                      </div>

                      {/* Services */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">Services Required *</label>
                        <div className="flex flex-wrap gap-1.5">
                          {serviceOptions.map((service) => (
                            <button
                              key={service}
                              type="button"
                              onClick={() => handleServiceToggle(service)}
                              className={`text-[11px] px-2.5 py-1 rounded-full border font-semibold transition-all duration-200 ${
                                formData.services.includes(service)
                                  ? 'bg-violet-600 border-violet-600 text-white'
                                  : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-violet-400 hover:text-violet-600'
                              }`}
                            >
                              {service}
                            </button>
                          ))}
                        </div>
                        {formErrors.services && <p className="text-red-500 text-[10px] mt-0.5">{formErrors.services}</p>}
                      </div>

                      {/* Message */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Message *</label>
                        <div className="relative">
                          <MessageCircle className="absolute left-2.5 top-2.5 text-gray-400" size={14} />
                          <textarea
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            rows={3}
                            placeholder="Tell us about your project..."
                            className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:outline-none text-xs transition-all resize-none dark:bg-gray-800 dark:text-white ${formErrors.message ? 'border-red-400' : 'border-gray-200 dark:border-gray-700 focus:border-violet-500'}`}
                          />
                        </div>
                        {formErrors.message && <p className="text-red-500 text-[10px] mt-0.5">{formErrors.message}</p>}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Submit — fixed at bottom */}
              {!submitSuccess && (
                <div className="flex-shrink-0 px-5 pb-4 pt-2 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white py-2.5 px-6 rounded-xl font-black text-sm hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-70"
                  >
                    {isSubmitting ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                        />
                        Sending...
                      </>
                    ) : (
                      <>Send Message <ArrowRight size={16} /></>
                    )}
                  </motion.button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <HomeHeroSection />
      <AdminPanelDemo />
      <EMSPortalDemo />
      <HomeServicesSection />
      <OurClientsSection />
      <WhyChooseUs />
      <PartnersSection />
      <TestimonialsSection />
      <HomeCTA />
    </div>
  );
};

export default HomePage;